import type { ReactNode } from 'react';
import type { FeaturedProject, WorkGroup as WorkGroupData } from '../../content/site';
import { ProjectArticle } from './ProjectArticle';

/** In-page anchor of a group (e.g. "groupe-erp"). */
export const groupAnchor = (id: string) => `groupe-${id}`;

const TEXT = 'text-graphite dark:text-mineral';
const MUTED = 'text-graphite-muted dark:text-dusk-muted';

/** Hairline between the articles of a group: spacing first, one line, never a box. */
const DIVIDE = 'divide-y divide-graphite/10 dark:divide-mineral/10';
const ROW = 'py-[clamp(3.5rem,7vw,6rem)] first:pt-0 last:pb-0';

/**
 * How the projects of a group are arranged, by group id (unknown groups stack):
 * - stack: each project full width, the lead composition (Volta, the flagship).
 * - suite: a structured set of tools. Projects with a recording or a live visual get a
 *   full row; the others sit in two columns, the ones with a screenshot on the left and
 *   the lighter ones stacked on the right.
 * - sites: the sites in one aligned row (three columns from 1024px, one on mobile).
 */
type Arrangement = 'stack' | 'suite' | 'sites';
const ARRANGEMENTS: Record<string, Arrangement> = { erp: 'stack', suite: 'suite', web: 'sites' };

const isWide = (p: FeaturedProject) => Boolean(p.videos?.length) || Boolean(p.ngram);
const isLight = (p: FeaturedProject) => !p.images.length && !isWide(p);

/** A column of articles separated by hairlines. */
function Column({ children }: { children: ReactNode }) {
	return <div className={`grid ${DIVIDE}`}>{children}</div>;
}

function Row({ children }: { children: ReactNode }) {
	return <div className={ROW}>{children}</div>;
}

function Pair({ children }: { children: ReactNode }) {
	return <div className="grid gap-y-[clamp(3.5rem,7vw,6rem)] lg:grid-cols-2 lg:items-start lg:gap-x-[clamp(2.5rem,6vw,5.5rem)]">{children}</div>;
}

function Projects({ group }: { group: WorkGroupData }) {
	const { projects } = group;
	const arrangement = ARRANGEMENTS[group.id] ?? 'stack';

	if (arrangement === 'suite') {
		// Consecutive projects that are not full-row ones are gathered into one two-column row.
		const rows: ReactNode[] = [];
		let run: FeaturedProject[] = [];
		const flush = () => {
			if (!run.length) return;
			const withMedia = run.filter((p) => !isLight(p));
			const light = run.filter(isLight);
			const columns = [withMedia, light].filter((c) => c.length);
			rows.push(
				<Row key={`run-${run[0].id}`}>
					<Pair>
						{columns.map((column) => (
							<Column key={column[0].id}>
								{column.map((p) => (
									<Row key={p.id}>
										<ProjectArticle project={p} size={isLight(p) && !p.channels?.length ? 'compact' : 'card'} />
									</Row>
								))}
							</Column>
						))}
					</Pair>
				</Row>,
			);
			run = [];
		};
		projects.forEach((p) => {
			if (isWide(p)) {
				flush();
				rows.push(
					<Row key={p.id}>
						<ProjectArticle project={p} size="lead" />
					</Row>,
				);
			} else {
				run.push(p);
			}
		});
		flush();
		return <div className={`grid ${DIVIDE}`}>{rows}</div>;
	}

	if (arrangement === 'sites') {
		// One row of equals: three columns from 1024px, each article as tall as the row with
		// its capture pinned to the bottom, so the captures line up whatever the text length.
		return (
			<Row>
				<div className="grid gap-y-[clamp(3.5rem,7vw,6rem)] lg:grid-cols-3 lg:gap-x-[clamp(1.5rem,3vw,2.5rem)]">
					{projects.map((p) => (
						<ProjectArticle key={p.id} project={p} size="card" pinMedia />
					))}
				</div>
			</Row>
		);
	}

	return (
		<div className={`grid ${DIVIDE}`}>
			{projects.map((p) => (
				<Row key={p.id}>
					<ProjectArticle project={p} size="lead" />
				</Row>
			))}
		</div>
	);
}

/**
 * One family of work. The heading (index, title, text) is fixed and spans the full width:
 * its content never changes while the projects below it are read. The optional closing
 * note ends the group.
 */
export function WorkGroup({ group }: { group: WorkGroupData }) {
	const headId = `${groupAnchor(group.id)}-titre`;
	return (
		<section id={groupAnchor(group.id)} aria-labelledby={headId}>
			<header
				data-work-group-head
				className="grid gap-y-5 border-t border-graphite/20 pt-8 dark:border-mineral/20 md:pt-10 lg:grid-cols-12 lg:gap-x-10"
			>
				<div className="flex items-baseline gap-4 sm:gap-6 lg:col-span-7">
					<span className={`font-mono text-[0.875rem] ${MUTED}`}>{group.index}</span>
					<h3
						id={headId}
						className={`font-display text-[clamp(2rem,5vw,4rem)] font-bold leading-[1] tracking-[-0.035em] ${TEXT}`}
					>
						{group.title}
					</h3>
				</div>
				<p className={`max-w-prose text-[1.0625rem] leading-relaxed lg:col-span-5 lg:pt-3 ${MUTED}`}>{group.text}</p>
			</header>

			<div className="mt-[clamp(2.5rem,5vw,4.5rem)]">
				<Projects group={group} />
			</div>

			{group.note && (
				<p
					data-work-group-head
					className={`mt-[clamp(3rem,6vw,5rem)] font-display text-[clamp(1.375rem,2.4vw,2rem)] font-medium leading-snug tracking-[-0.02em] ${TEXT}`}
				>
					{group.note}
				</p>
			)}
		</section>
	);
}
